import { typeGuardHelpers } from '@approvalmax/utils';
import { useMemo } from 'react';
import { messages } from 'shared/constants/countryNames';
import { Role } from 'shared/data/walletsKyc/v1';
import { usePersonCountries } from 'shared/hooks/(wallets)/kyc/usePersonCountries';

import { useKycData } from '../../../../../resources/hooks/useKycData';

export const useSummaryFields = () => {
    const { data, isLoading } = useKycData();
    const { countries, isCountriesLoading } = usePersonCountries();

    const { directors, owners } = useMemo(
        () =>
            (data?.data?.people?.persons || []).reduce<{
                directors: string[];
                owners: string[];
            }>(
                (acc, person) => {
                    if (person.roles.includes(Role.director)) {
                        acc.directors.push(`${person.firstName} ${person.lastName}`);
                    } else if (person.roles.includes(Role.ultimate_business_owner)) {
                        acc.owners.push(`${person.firstName} ${person.lastName}`);
                    }

                    return acc;
                },
                { directors: [], owners: [] }
            ),
        [data?.data?.people?.persons]
    );

    const summaryFields = useMemo(
        () => ({
            companyName: data?.data?.businessInformation?.companyName,
            destCountries: data?.data?.accountUsage?.transactionCountries
                .map((countryCode) =>
                    typeGuardHelpers.isKeyOfObject(countryCode, messages)
                        ? messages[countryCode]
                        : countries.find((country) => country.id === countryCode)?.name
                )
                .join(', '),
            currenciesPayOut: data?.data?.accountUsage?.paymentCurrencies.join(', '),
            currenciesSendIn: data?.data?.accountUsage?.collectionCurrencies.join(', '),
            directors: directors.join(', '),
            owners: owners.join(', '),
        }),
        [
            countries,
            data?.data?.accountUsage?.collectionCurrencies,
            data?.data?.accountUsage?.paymentCurrencies,
            data?.data?.accountUsage?.transactionCountries,
            data?.data?.businessInformation?.companyName,
            directors,
            owners,
        ]
    );

    return { ...summaryFields, isLoading, isCountriesLoading };
};
